/**
 * Presentational card for a single GO Lab dataset collection on the
 * Datasets home grid. Stays presentational — navigation lives on the
 * page (which wraps each card in a `<Link>`), so the molecule has no
 * router import and is straightforward to fixture in Storybook.
 */

import React from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '../../../../../ui/card';
import { Badge } from '../../../../../ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../ui/tooltip';
import { CHANNELS, type ChannelKey } from '../../../shared/channels';
import { formatTimeAgo } from '../../../../../../utils/dateUtils';
import type { CollectionSummary } from '../../types';

interface Props {
  summary: CollectionSummary;
  /** Resolved taxonomy display name from `useTaxonomyKeys`; falls back to the raw key upstream. */
  taxonomyDisplayName: string;
}

const CollectionCard: React.FC<Props> = ({ summary, taxonomyDisplayName }) => {
  const { name, status, region, industry, channels, stats, channelCount, lastActivityAt } = summary;

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base font-semibold">{name}</CardTitle>
          <Badge
            variant={status === 'active' ? 'default' : 'secondary'}
            className="capitalize"
            data-testid="collection-status-badge"
          >
            {status}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="collection-locale">
          {region} · {industry} · {taxonomyDisplayName}
        </p>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col justify-between gap-4">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Channels
          </p>
          {channels.length === 0 ? (
            <span className="text-xs text-muted-foreground" data-testid="collection-no-channels">
              —
            </span>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="collection-channel-dots">
              {channels.map((ch: ChannelKey) => (
                <Tooltip key={ch}>
                  <TooltipTrigger
                    className="inline-block h-3 w-3 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    style={{ backgroundColor: CHANNELS[ch].color }}
                    aria-label={CHANNELS[ch].name}
                    data-testid={`channel-dot-${ch}`}
                  />
                  <TooltipContent>{CHANNELS[ch].name}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        <dl className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <dt className="text-muted-foreground">Channels</dt>
            <dd className="font-semibold tabular-nums">{channelCount}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Records</dt>
            <dd className="font-semibold tabular-nums">{stats.totalRecords.toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Finalised</dt>
            <dd className="font-semibold tabular-nums">{stats.finalised.toLocaleString()}</dd>
          </div>
        </dl>

        <p className="text-[11px] text-muted-foreground" data-testid="collection-last-activity">
          {lastActivityAt ? `Last activity ${formatTimeAgo(lastActivityAt)}` : 'No activity yet'}
        </p>
      </CardContent>
    </Card>
  );
};

export default CollectionCard;
